import { FC, useEffect, useMemo, useRef } from 'react';
import { graphql, useLazyLoadQuery } from 'react-relay/hooks';
import { discoveryCollagePrefetchQuery } from './__generated__/discoveryCollagePrefetchQuery.graphql';

// Prefetches the real Collage Layouts catalog for discovery mode instead of hardcoding it.
//
// The customizer's useDiscoveryGridRegistrar projects the streamed `{ tool: 'collage-layouts', grids, gridRules }`
// payload straight into store.grids / store.gridRules (see that hook), so the collage boxes render on a sandbox
// product with no grid rules configured. We build that payload from the same `gridList` query the Collage Layouts
// rule form uses, filtered to the SYSTEM/default grids (sellerID == null) — the shared set that is identical for
// every merchant and shown (non-editable) in the rule picker. This keeps discovery and the rule form in sync by
// construction: whatever system layouts exist (Heart included) show in both, with no generated json/svg to maintain.

// The Grid schema type carries a native numeric gridID (Long), which is exactly what the customizer's Grid /
// GridAssociation key on — no id conversion needed.
type GridListEntry = NonNullable<discoveryCollagePrefetchQuery['response']['gridList'][number]>;

const DISCOVERY_COLLAGE_GRID_RULE_ID = 999001;

// Map the fetched system grids into the customizer's discovery collage payload. Both the grids and the single
// synthetic "all products / all sides" grid rule are built from the same gridID, so their association is
// internally consistent regardless of the actual id values.
const buildDiscoveryCollagePayload = (gridList: readonly (GridListEntry | null)[]): object => {
    const grids = (gridList ?? [])
        .filter((g): g is GridListEntry => g != null && g.sellerID == null && !g.isDeleted && !!g.json && !!g.svg)
        .map((g) => ({
            gridID: g.gridID,
            name: g.name ?? '',
            json: g.json as string,
            svg: g.svg as string,
            sellerID: null,
            isDeleted: false
        }));

    const gridRules = [
        {
            gridRuleID: DISCOVERY_COLLAGE_GRID_RULE_ID,
            name: 'Discovery collage layouts',
            sellerID: 0,
            grids: [],
            associations: grids.map((g) => ({
                gridRuleID: DISCOVERY_COLLAGE_GRID_RULE_ID,
                gridID: g.gridID,
                productID: null,
                sideID: null
            }))
        }
    ];

    return { tool: 'collage-layouts', grids, gridRules };
};

// Rendered only while discovery mode is active. Runs the gridList query and hands the built payload up via
// onLoaded so the parent can stream it on the Collage Layouts click without any per-click fetch latency.
export const DiscoveryCollagePrefetch: FC<{ onLoaded: (payload: object) => void }> = ({ onLoaded }) => {
    const data = useLazyLoadQuery<discoveryCollagePrefetchQuery>(
        graphql`
            query discoveryCollagePrefetchQuery {
                gridList {
                    gridID
                    name
                    svg
                    json
                    sellerID
                    isDeleted
                }
            }
        `,
        {},
        { fetchPolicy: 'store-or-network' }
    );

    const payload = useMemo(() => buildDiscoveryCollagePayload(data.gridList), [data.gridList]);

    // Keep onLoaded off the effect deps so an inline arrow from the parent doesn't re-fire it every render.
    const onLoadedRef = useRef(onLoaded);
    onLoadedRef.current = onLoaded;
    useEffect(() => {
        onLoadedRef.current(payload);
    }, [payload]);

    return null;
};
